if (!customElements.get('size-guide-drawer')) {
  customElements.define(
    'size-guide-drawer',
    class SizeGuideDrawer extends HTMLElement {
      constructor() {
        super();
        this.openModal = this.openModal.bind(this);
        this.closeModal = this.closeModal.bind(this);
        this.init = this.init.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.previouslyFocusedElement = null;
      }

      connectedCallback() {
        this.titleElement = this.querySelector('[data-size-guide-title]');
        this.contentElement = this.querySelector('[data-size-guide-content]');
        this.closeElements = this.querySelectorAll('[data-size-guide-close]');
        this.panel = this.querySelector('.size-guide-modal__panel');

        if (window.wetheme && window.wetheme.webcomponentRegistry) {
          window.wetheme.webcomponentRegistry.register({ key: 'component-size-guide-drawer' });
        }

        this.closeElements.forEach((element) => {
          element.addEventListener('click', this.closeModal);
        });

        if (window.eventBus && typeof window.eventBus.on === 'function') {
          window.eventBus.on('open:size:guide', this.init);
          window.eventBus.on('close:size:guide', this.closeModal);
        }

        document.addEventListener('open:size:guide', this.init);
      }

      disconnectedCallback() {
        this.closeElements.forEach((element) => {
          element.removeEventListener('click', this.closeModal);
        });

        if (window.eventBus && typeof window.eventBus.off === 'function') {
          window.eventBus.off('open:size:guide', this.init);
          window.eventBus.off('close:size:guide', this.closeModal);
        }

        document.removeEventListener('open:size:guide', this.init);
        document.removeEventListener('keydown', this.handleKeydown);
      }

      async init(event) {
        const payload = event && event.detail ? event.detail : event || {};
        const title = payload.title || 'Size Guide';

        this.setTitle(title);
        this.setLoading();
        this.openModal(payload.source);

        if (payload.html) {
          this.renderHTML(payload.html);
          return;
        }

        if (payload.url) {
          await this.loadURL(payload.url, title);
          return;
        }

        this.renderEmpty();
      }

      openModal(source) {
        this.previouslyFocusedElement =
          source && source.querySelector ? source.querySelector('[data-size-guide-link]') : document.activeElement;

        this.setAttribute('aria-hidden', 'false');
        document.body.classList.add('size-guide-modal-open');
        document.addEventListener('keydown', this.handleKeydown);

        requestAnimationFrame(() => {
          const closeButton = this.querySelector('[data-size-guide-close]:not(.size-guide-modal__overlay)');
          if (closeButton) closeButton.focus();
        });
      }

      closeModal() {
        this.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('size-guide-modal-open');
        document.removeEventListener('keydown', this.handleKeydown);

        if (this.previouslyFocusedElement && typeof this.previouslyFocusedElement.focus === 'function') {
          this.previouslyFocusedElement.focus();
        }
      }

      handleKeydown(event) {
        if (this.getAttribute('aria-hidden') === 'true') return;

        if (event.key === 'Escape') {
          this.closeModal();
          return;
        }

        if (event.key !== 'Tab') return;

        const focusableElements = this.querySelectorAll(
          'button, [href], iframe, input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const visibleFocusableElements = Array.from(focusableElements).filter((element) => {
          return !element.hasAttribute('disabled') && element.offsetParent !== null;
        });

        if (!visibleFocusableElements.length) return;

        const firstElement = visibleFocusableElements[0];
        const lastElement = visibleFocusableElements[visibleFocusableElements.length - 1];

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }

      async loadURL(url, fallbackTitle) {
        if (this.isPDF(url) || this.isImage(url)) {
          this.renderAsset(url, fallbackTitle);
          return;
        }

        try {
          const response = await fetch(url);
          if (!response.ok) throw new Error(`Failed to fetch size guide: ${response.status}`);

          const html = await response.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const pageTitle = doc.querySelector('.main-page-content [data-page-title], .main-page-content h1, h1');
          const pageContent = doc.querySelector('.main-page-content, main');

          if (pageTitle && !fallbackTitle) {
            this.setTitle(pageTitle.textContent.trim());
          }

          if (pageContent) {
            this.contentElement.innerHTML = '';
            this.contentElement.appendChild(pageContent.cloneNode(true));
          } else {
            this.renderAsset(url, fallbackTitle);
          }
        } catch (error) {
          console.error(error);
          this.contentElement.innerHTML = `
            <p class="size-guide-modal__empty">
              The size guide could not be loaded here.
              <a href="${this.escapeHTML(url)}" target="_blank" rel="noopener noreferrer">Open it in a new tab</a>.
            </p>
          `;
        }
      }

      renderHTML(html) {
        this.contentElement.innerHTML = html;
      }

      renderAsset(url, title) {
        if (this.isPDF(url)) {
          this.contentElement.innerHTML = `
            <div class="size-guide__pdf-frame">
              <iframe src="${this.escapeHTML(url)}" title="${this.escapeHTML(title || 'Size guide PDF')}" loading="lazy"></iframe>
            </div>
            <p class="size-guide__pdf-fallback">
              <a href="${this.escapeHTML(url)}" target="_blank" rel="noopener noreferrer">Open size guide PDF</a>
            </p>
          `;
          return;
        }

        this.contentElement.innerHTML = `
          <div class="size-guide__media-frame">
            <img src="${this.escapeHTML(url)}" alt="${this.escapeHTML(title || 'Size guide')}" class="size-guide__image" loading="lazy">
          </div>
        `;
      }

      setTitle(title) {
        if (this.titleElement) {
          this.titleElement.textContent = title || 'Size Guide';
        }
      }

      setLoading() {
        if (this.contentElement) {
          this.contentElement.innerHTML = '<p class="size-guide-modal__loading">Loading size guide...</p>';
        }
      }

      renderEmpty() {
        this.contentElement.innerHTML = '<p class="size-guide-modal__empty">No size guide content has been added yet.</p>';
      }

      isPDF(url) {
        return this.getURLPath(url).endsWith('.pdf');
      }

      isImage(url) {
        return /\.(png|jpe?g|gif|webp|avif|svg)$/i.test(this.getURLPath(url));
      }

      getURLPath(url) {
        try {
          return new URL(url, window.location.origin).pathname.toLowerCase();
        } catch (error) {
          return String(url).split('?')[0].split('#')[0].toLowerCase();
        }
      }

      escapeHTML(value) {
        return String(value || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;');
      }
    }
  );
}
